import { useRef, useState, useEffect, useCallback } from "react";

export default function useAudioPlayer(src) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRateState] = useState(1);
  const [volume, setVolumeState] = useState(1);
  const [ready, setReady] = useState(false);
  const loopRef = useRef(null); // {start, end}
  const ctxRef = useRef(null);
  const graphRef = useRef(null); // {low, mid, high}
  const pendingBands = useRef({ low: 1, mid: 1, high: 1 });

  const applyBands = useCallback((bands) => {
    const g = graphRef.current;
    const ctx = ctxRef.current;
    if (!g || !ctx) return;
    ["low", "mid", "high"].forEach((b) => {
      const v = Math.max(0, Math.min(1.4, bands[b] ?? 1));
      g[b].gain.setTargetAtTime(v, ctx.currentTime, 0.03);
    });
  }, []);

  const ensureGraph = useCallback(() => {
    if (graphRef.current || !audioRef.current) return;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    try {
      const ctx = new Ctx();
      ctxRef.current = ctx;
      const source = ctx.createMediaElementSource(audioRef.current);
      const master = ctx.createGain();
      const lowGain = ctx.createGain();
      const midGain = ctx.createGain();
      const highGain = ctx.createGain();
      const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 260;
      const hp1 = ctx.createBiquadFilter(); hp1.type = "highpass"; hp1.frequency.value = 260;
      const lp2 = ctx.createBiquadFilter(); lp2.type = "lowpass"; lp2.frequency.value = 3600;
      const hp2 = ctx.createBiquadFilter(); hp2.type = "highpass"; hp2.frequency.value = 3600;
      source.connect(lp); lp.connect(lowGain); lowGain.connect(master);
      source.connect(hp1); hp1.connect(lp2); lp2.connect(midGain); midGain.connect(master);
      source.connect(hp2); hp2.connect(highGain); highGain.connect(master);
      master.connect(ctx.destination);
      graphRef.current = { low: lowGain, mid: midGain, high: highGain, master };
      applyBands(pendingBands.current);
    } catch (e) {
      // MediaElementSource can only be created once / needs same-origin; fall back to plain playback
      console.warn("Audio EQ graph unavailable, using plain playback:", e?.message || e);
    }
  }, [applyBands]);

  const setBands = useCallback((bands) => {
    pendingBands.current = bands;
    if (typeof window !== "undefined") window.__songwiseBands = bands;
    applyBands(bands);
  }, [applyBands]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => {
      setCurrentTime(audio.currentTime);
      const loop = loopRef.current;
      if (loop && audio.currentTime >= loop.end) {
        audio.currentTime = loop.start;
      }
    };
    const onMeta = () => {
      if (audio.duration && isFinite(audio.duration)) setDuration(audio.duration);
      setReady(true);
    };
    const onEnd = () => setPlaying(false);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("canplay", onMeta);
    audio.addEventListener("ended", onEnd);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("canplay", onMeta);
      audio.removeEventListener("ended", onEnd);
    };
  }, [src]);

  const play = useCallback(() => {
    ensureGraph();
    if (ctxRef.current?.state === "suspended") ctxRef.current.resume();
    audioRef.current?.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  }, [ensureGraph]);
  const pause = useCallback(() => {
    audioRef.current?.pause();
    setPlaying(false);
  }, []);
  const toggle = useCallback(() => {
    if (playing) pause();
    else play();
  }, [playing, play, pause]);

  const seek = useCallback((t) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, t);
      setCurrentTime(t);
    }
  }, []);

  const setRate = useCallback((r) => {
    if (audioRef.current) audioRef.current.playbackRate = r;
    setRateState(r);
  }, []);

  const setVolume = useCallback((v) => {
    if (audioRef.current) audioRef.current.volume = v;
    setVolumeState(v);
  }, []);

  const setLoop = useCallback((region) => {
    loopRef.current = region;
  }, []);

  return {
    audioRef, playing, currentTime, duration: duration, ready,
    rate, volume, play, pause, toggle, seek, setRate, setVolume, setLoop, setBands,
  };
}
